/**
 * OnboardingWizard — first-run guided setup for a new reseller.
 *
 * Walks through:
 *   1. Welcome — what BuyerGram is + what we're about to set up
 *   2. FX rate — the $→₽ rate the reseller marks up cards at
 *   3. Forward target — the TG channel @Buyergrambot will push to
 *   4. Done — explicit "open the catalog" CTA
 *
 * Each step's input is committed via PATCH /me/reseller-settings as
 * the user advances, so partial progress survives a refresh. Once all
 * four steps are complete the localStorage flag
 *   bg_onboarding_done:<userId>
 * is set and the wizard never re-triggers for this account.
 *
 * Trigger logic lives in MyStore (the consumer) — render this component
 * conditionally when:
 *   - role === "customer"
 *   - reseller-settings has no forward target
 *   - localStorage flag isn't set
 */
import { useState, useEffect } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Mono, Serif, Btn, useSwipeDownDismiss } from "./ui.jsx";
import { useT } from "../i18n.jsx";

const FX_FALLBACK = 95;        // sensible 2026 ₽/$ default if we can't read
                                // the platform value (customer role has no
                                // access to /admin/settings/fx).

const STEPS = [
  { id: "welcome",  labelKey: "onboarding.step.welcome" },
  { id: "fx",       labelKey: "onboarding.step.fx" },
  { id: "target",   labelKey: "onboarding.step.target" },
  { id: "done",     labelKey: "onboarding.step.done" },
];

export function OnboardingWizard({ user, onClose, toast }) {
  const t = useT();
  const [stepIdx, setStepIdx] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [fxRate, setFxRate] = useState(FX_FALLBACK);
  const [targetKind, setTargetKind] = useState("telegram"); // "telegram" | "max"
  const [targetChatId, setTargetChatId] = useState("");
  const [targetLabel, setTargetLabel] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.getResellerSettings();
        if (cancelled) return;
        setSettings(s);
        if (Number(s.fxRateRubPerUsd) > 0) setFxRate(Number(s.fxRateRubPerUsd));
        // If the reseller already filled in a target before, surface
        // the first one so the wizard doesn't ask for a duplicate.
        const existing = (s.forwardTargets || []).find((t) => t.chatId);
        if (existing) {
          setTargetKind(existing.kind || "telegram");
          setTargetChatId(existing.chatId);
          setTargetLabel(existing.label || "");
        }
      } catch {
        // Default to fallback; reseller can still finish wizard.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persistFx = async () => {
    if (!(fxRate > 0)) {
      toast?.error?.(t("onboarding.fx.err.positive"));
      return false;
    }
    setSaving(true);
    try {
      await api.patchResellerSettings({ fxRateRubPerUsd: fxRate });
      return true;
    } catch (e) {
      toast?.error?.(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const persistTarget = async () => {
    const trimmed = targetChatId.trim();
    if (!trimmed) {
      toast?.error?.(t("onboarding.target.err.empty"));
      return false;
    }
    // Soft format check — @username, -100xxxxxxxxxx (Telegram channel
    // ID), or a positive numeric id. The actual send will surface a
    // real error later if Telegram rejects it; this catches obvious
    // copy-paste mistakes (URLs, spaces, the bot's own username).
    const looksLikeUsername = /^@?[A-Za-z][A-Za-z0-9_]{4,31}$/.test(trimmed);
    const looksLikeChannelId = /^-?\d{6,}$/.test(trimmed);
    if (!looksLikeUsername && !looksLikeChannelId) {
      toast?.error?.(t("onboarding.target.err.format"));
      return false;
    }
    setSaving(true);
    try {
      // Read current targets, append or replace the single first target.
      // Wizard-mode resellers typically have at most one channel, so
      // this is a safe simplification — the full Settings tab is
      // available for multi-target editing later.
      const current = settings?.forwardTargets || [];
      const next = current.length > 0
        ? current.map((t, i) => i === 0 ? {
            ...t,
            kind: targetKind,
            chatId: targetChatId.trim(),
            label: targetLabel.trim() || t.label || "",
            enabled: true,
          } : t)
        : [{
            id: crypto.randomUUID
              ? crypto.randomUUID()
              : `t-${Date.now()}`,
            kind: targetKind,
            chatId: targetChatId.trim(),
            label: targetLabel.trim() || "",
            enabled: true,
          }];
      await api.patchResellerSettings({ forwardTargets: next });
      // Re-pull so the next step has fresh data + correct IDs.
      const fresh = await api.getResellerSettings();
      setSettings(fresh);
      return true;
    } catch (e) {
      toast?.error?.(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const advance = async () => {
    const id = STEPS[stepIdx].id;
    if (id === "fx" && !(await persistFx())) return;
    if (id === "target" && !(await persistTarget())) return;
    if (stepIdx >= STEPS.length - 1) {
      finish();
      return;
    }
    setStepIdx((i) => i + 1);
  };
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  const finish = () => {
    try {
      localStorage.setItem(`bg_onboarding_done:${user?.uid || ""}`, "1");
    } catch {
      // localStorage may be unavailable (private browsing); the wizard
      // just re-runs next visit, which is annoying but not broken.
    }
    onClose?.();
  };

  const { ref: sheetRef, dy, dismissing } = useSwipeDownDismiss(finish);
  const dragging = dy > 0;
  const scrim = dragging ? Math.max(0.05, 0.55 - dy / 900) : 0.55;

  const step = STEPS[stepIdx];

  return (
    <div className="cg-modal-bg"
         style={{
           position: "fixed", inset: 0,
           background: `rgba(20,17,13,${scrim})`,
           transition: dragging ? "none" : "background-color 180ms",
           pointerEvents: dismissing ? "none" : "auto",
           display: "flex", alignItems: "center", justifyContent: "center",
           zIndex: 200, padding: 16,
         }}>
      <div ref={sheetRef} className="cg-modal-body"
           style={{
             background: T_NEW.paperRaised,
             borderRadius: T_NEW.r3, padding: 24,
             maxWidth: 520, width: "100%",
             maxHeight: "92dvh", overflowY: "auto",
             boxShadow: T_NEW.shadowModal,
             fontFamily: T_NEW.fontSans,
             transform: `translateY(${dy}px)`,
             transition: dragging ? "none"
                                  : "transform 220ms cubic-bezier(0.2,0,0.0,1.0)",
           }}>
        <div className="cg-modal-grab" aria-hidden />

        {/* Step indicator strip */}
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{
              flex: 1, height: 3, borderRadius: 999,
              background: i <= stepIdx ? T_NEW.persimmon : T_NEW.ink12,
              transition: "background 200ms",
            }} />
          ))}
        </div>
        <Mono color={T_NEW.persimmon}
              style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
          {t("onboarding.stepIndicator", { idx: stepIdx + 1, total: STEPS.length, label: t(step.labelKey).toUpperCase() })}
        </Mono>

        {loading ? (
          <p style={{ color: T_NEW.ink56, padding: "24px 0" }}>{t("onboarding.loading")}</p>
        ) : (
          <>
            {step.id === "welcome" && (
              <WelcomeStep t={t} />
            )}
            {step.id === "fx" && (
              <FxStep fxRate={fxRate} setFxRate={setFxRate} t={t} />
            )}
            {step.id === "target" && (
              <TargetStep
                kind={targetKind} setKind={setTargetKind}
                chatId={targetChatId} setChatId={setTargetChatId}
                label={targetLabel} setLabel={setTargetLabel}
                t={t}
              />
            )}
            {step.id === "done" && (
              <DoneStep t={t} />
            )}
          </>
        )}

        {/* Footer: Back + Primary action */}
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginTop: 24, gap: 10 }}>
          {stepIdx === 0 ? (
            <button onClick={finish}
                    style={{
                      background: "transparent", border: "none",
                      color: T_NEW.ink56, fontFamily: T_NEW.fontMono,
                      fontSize: T_NEW.micro, textTransform: "uppercase",
                      letterSpacing: T_NEW.monoTrack, cursor: "pointer",
                      padding: 0, fontWeight: 700,
                    }}>
              {t("onboarding.skip")}
            </button>
          ) : (
            <Btn variant="ghost" onClick={back} disabled={saving}>
              {t("onboarding.back")}
            </Btn>
          )}
          <Btn variant="accent" onClick={advance} disabled={loading || saving}>
            {saving ? t("onboarding.saving")
                    : stepIdx === STEPS.length - 1 ? t("onboarding.finish")
                                                    : t("onboarding.next")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function WelcomeStep({ t }) {
  return (
    <div>
      <Serif size={T_NEW.hero}
             style={{ fontWeight: 500, lineHeight: 1.1, marginBottom: 12,
                      letterSpacing: "-0.01em" }}>
        {t("onboarding.welcome.title")}
      </Serif>
      <p style={{ color: T_NEW.ink, fontSize: T_NEW.body,
                  lineHeight: 1.6, margin: 0 }}>
        {t("onboarding.welcome.body")}
      </p>
      <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta,
                  lineHeight: 1.55, marginTop: 14, marginBottom: 0 }}>
        {t("onboarding.welcome.foot")}
      </p>
    </div>
  );
}

function FxStep({ fxRate, setFxRate, t }) {
  // Sanity warning when the rate falls outside the typical 2026 range
  // (60–200 ₽/$). Doesn't block — the reseller may have niche reasons
  // — just nudges them to double-check.
  const unusualLow = fxRate > 0 && fxRate < 60;
  const unusualHigh = fxRate > 200;
  return (
    <div>
      <Serif size={T_NEW.displaySm}
             style={{ fontWeight: 500, lineHeight: 1.25, marginBottom: 8 }}>
        {t("onboarding.fx.title")}
      </Serif>
      <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta,
                  lineHeight: 1.55, marginBottom: 16 }}>
        {t("onboarding.fx.body")}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          type="number" min={1} max={500} step={0.1}
          value={fxRate}
          onChange={(e) => setFxRate(parseFloat(e.target.value) || 0)}
          style={{
            flex: 1, padding: "12px 14px",
            background: T_NEW.paperSoft,
            border: `1px solid ${T_NEW.ink12}`,
            borderRadius: T_NEW.r1,
            fontSize: T_NEW.displaySm,
            fontFamily: T_NEW.fontSerif, fontWeight: 500,
            color: T_NEW.ink, textAlign: "right",
            outline: "none",
          }}
        />
        <Mono color={T_NEW.ink56} size={T_NEW.body}>{t("onboarding.fx.unit")}</Mono>
      </div>
      <Mono color={T_NEW.ink38} size={T_NEW.micro}
            style={{ display: "block", marginTop: 8 }}>
        {t("onboarding.fx.example", { rub: Math.round(49.99 * (fxRate || 0)) })}
      </Mono>
      {(unusualLow || unusualHigh) && (
        <div style={{
          marginTop: 10, padding: "6px 10px",
          background: T_NEW.ochreSoft,
          border: `1px solid ${T_NEW.ochre}33`,
          borderRadius: T_NEW.r1,
          fontSize: T_NEW.micro, color: T_NEW.ink,
          lineHeight: 1.4,
        }}>
          {unusualLow
            ? t("onboarding.fx.unusualLow")
            : t("onboarding.fx.unusualHigh")}
        </div>
      )}
    </div>
  );
}

function TargetStep({ kind, setKind, chatId, setChatId, label, setLabel, t }) {
  return (
    <div>
      <Serif size={T_NEW.displaySm}
             style={{ fontWeight: 500, lineHeight: 1.25, marginBottom: 8 }}>
        {t("onboarding.target.title")}
      </Serif>
      <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta,
                  lineHeight: 1.55, marginBottom: 16 }}>
        {t("onboarding.target.body")}
      </p>

      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
        {t("onboarding.target.platform")}
      </Mono>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[
          { id: "telegram", label: t("onboarding.target.kind.tg") },
          { id: "max", label: t("onboarding.target.kind.max") },
        ].map((opt) => (
          <button key={opt.id}
                  onClick={() => setKind(opt.id)}
                  style={{
                    flex: 1, padding: "10px 12px",
                    background: kind === opt.id ? T_NEW.ink : "transparent",
                    color: kind === opt.id ? T_NEW.paperRaised : T_NEW.ink,
                    border: `1px solid ${kind === opt.id
                      ? T_NEW.ink : T_NEW.ink12}`,
                    borderRadius: T_NEW.r1,
                    fontFamily: T_NEW.fontSans,
                    fontSize: T_NEW.body, fontWeight: 600,
                    cursor: "pointer",
                  }}>
            {opt.label}
          </button>
        ))}
      </div>

      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
        {t("onboarding.target.chatId")}
      </Mono>
      <input
        type="text" value={chatId}
        onChange={(e) => setChatId(e.target.value)}
        placeholder={kind === "telegram" ? t("onboarding.target.chatId.placeholder.tg")
                                          : t("onboarding.target.chatId.placeholder.max")}
        style={{
          width: "100%", padding: "10px 12px",
          background: T_NEW.paperSoft,
          border: `1px solid ${T_NEW.ink12}`,
          borderRadius: T_NEW.r1,
          fontSize: T_NEW.body, fontFamily: T_NEW.fontMono,
          color: T_NEW.ink, boxSizing: "border-box", outline: "none",
          marginBottom: 12,
        }}
      />

      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
        {t("onboarding.target.label")}
      </Mono>
      <input
        type="text" value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={t("onboarding.target.label.placeholder")}
        style={{
          width: "100%", padding: "10px 12px",
          background: T_NEW.paperSoft,
          border: `1px solid ${T_NEW.ink12}`,
          borderRadius: T_NEW.r1,
          fontSize: T_NEW.body, fontFamily: T_NEW.fontSans,
          color: T_NEW.ink, boxSizing: "border-box", outline: "none",
        }}
      />
    </div>
  );
}

function DoneStep({ t }) {
  return (
    <div>
      <Serif size={T_NEW.hero}
             style={{ fontWeight: 500, lineHeight: 1.1, marginBottom: 12,
                      letterSpacing: "-0.01em" }}>
        {t("onboarding.done.title")}
      </Serif>
      <p style={{ color: T_NEW.ink, fontSize: T_NEW.body,
                  lineHeight: 1.6, margin: 0, marginBottom: 14 }}>
        {t("onboarding.done.body")}
      </p>
      <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta,
                  lineHeight: 1.55, margin: 0 }}>
        {t("onboarding.done.foot")}
      </p>
    </div>
  );
}
